import { useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export type SortValueGetters<T> = Record<
  string,
  (row: T) => string | number | null | undefined
>;

// Generic client-side row sorter for the plain-<table> views in
// HaulersMonitorView / DispatchPlanV2View / ManualExcavatorPanel — none of
// them use a grid library, so there's no built-in column sort to hook into.
// `getters` maps a column key to a function that pulls the comparable value
// out of a row; nullish values always sort last regardless of direction.
//
// Deliberately not memoized: `getters` closes over live state (statuses,
// staged edits, ...) that changes independently of `rows`, so memoizing on
// `rows`/sortKey/direction alone would show stale order after a live update.
// Row counts here are small (tens, not thousands), so re-sorting every
// render is cheap — correctness wins over a memo that would need `getters`
// recreated fresh (and thus never actually skip work) to stay correct anyway.
export function useSortableRows<T>(rows: T[], getters: SortValueGetters<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [direction, setDirection] = useState<SortDirection>('asc');

  const getValue = sortKey ? getters[sortKey] : null;
  let sortedRows = rows;
  if (getValue) {
    const withValue = rows.map((row) => ({ row, value: getValue(row) }));
    withValue.sort((a, b) => {
      if (a.value == null && b.value == null) return 0;
      if (a.value == null) return 1;
      if (b.value == null) return -1;
      if (typeof a.value === 'number' && typeof b.value === 'number') {
        return a.value - b.value;
      }
      return String(a.value).localeCompare(String(b.value), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });
    const ordered = withValue.map((entry) => entry.row);
    sortedRows = direction === 'asc' ? ordered : ordered.reverse();
  }

  const toggleSort = (key: string) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setDirection((prevDirection) =>
          prevDirection === 'asc' ? 'desc' : 'asc'
        );
        return key;
      }
      setDirection('asc');
      return key;
    });
  };

  return { sortedRows, sortKey, direction, toggleSort };
}
